import { createElement, useCallback, useMemo } from "react";
import type { FilterMenuConfig } from "@/shared/models/filterTypes";
import type { MatrixRow } from "@/ui-components/FilterPanel/MatrixSection";
import { formatListName } from "@/shared/utils/formatListName";
import {
  FilterConflictNote,
  FILTER_OWNERS,
  type FilterOwner,
} from "@/shared/components/FilterComponents";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { isOrOnlyCombined } from "@/shared/utils/combinedFilters";

/**
 * The filter/highlight matrix, shared by every feed widget.
 *
 * Whether a list holds tickers, keywords or tags is our implementation detail,
 * so they share ONE list here — each row carrying the selection keys its
 * checkbox writes to, rather than the user navigating by storage type.
 *
 * The verb stays split: highlighting and filtering are functionally different
 * and each has its own widget-header indicator, so each panel renders one of
 * them (`column`). Sharing rows between the two panels keeps them consistent
 * without pretending they're the same action.
 */

/** Watchlist sections that merge into one table, in display order. */
const WATCHLIST_FAMILY = [
  { filterId: "ticker-watchlists-filter", highlightId: "ticker-watchlists-highlight", kind: "T" },
  { filterId: "keyword-watchlists-filter", highlightId: "keyword-watchlists-highlight", kind: "K" },
  { filterId: "tag-watchlists-filter", highlightId: "tag-watchlists-highlight", kind: "#" },
];

const WATCHLIST_LABEL = "Watchlists";

/** Owner grouping, from the `user-*` / `newsquawk-*` subsection convention. */
function ownerOf(sectionId: string, optionId: string, menu?: FilterMenuConfig): string {
  const sub = menu?.subsections?.find((s) => s.filterIds.map(String).includes(optionId));
  if (sub?.id.startsWith("newsquawk-")) return "Newsquawk";
  if (sub?.id.startsWith("user-")) return "Yours";
  return sectionId.startsWith("category") ? "Newsquawk" : "Yours";
}

export interface UseFilterHighlightMatrixOptions {
  highlightMenus: FilterMenuConfig[];
  highlightSelections: Record<string, string[]>;
  setHighlightSelections: (next: Record<string, string[]>) => void;
  filterMenus: FilterMenuConfig[];
  filterSelections: Record<string, string[]>;
  setFilterSelections: (next: Record<string, string[]>) => void;
}

export interface MatrixGroup {
  id: string;
  label: string;
  rows: MatrixRow[];
  conflictNote?: React.ReactNode;
  highlightCount: number;
  filterCount: number;
}

export interface UseFilterHighlightMatrixReturn {
  /** Merged tables to render, in order. */
  groups: MatrixGroup[];
  /** Sections that aren't lists (asset class, sources) — rendered as before. */
  passthrough: FilterMenuConfig[];
  selections: Record<string, string[]>;
  setSelections: (next: Record<string, string[]>) => void;
  /** Active count per verb — each panel reports only its own. */
  highlightTotal: number;
  filterTotal: number;
}

export function useFilterHighlightMatrix({
  highlightMenus,
  highlightSelections,
  setHighlightSelections,
  filterMenus,
  filterSelections,
  setFilterSelections,
}: UseFilterHighlightMatrixOptions): UseFilterHighlightMatrixReturn {
  const selections = useMemo(
    // Section ids are disjoint between the two states, so a flat merge is
    // lossless and gives the table one map to read.
    () => ({ ...filterSelections, ...highlightSelections }),
    [filterSelections, highlightSelections]
  );

  const highlightIds = useMemo(
    () =>
      new Set(
        highlightMenus
          .map((menu) => menu.id)
          .concat(WATCHLIST_FAMILY.map((f) => f.highlightId))
          .concat("category-highlight")
      ),
    [highlightMenus]
  );

  const setSelections = useCallback(
    (next: Record<string, string[]>) => {
      const nextHighlight: Record<string, string[]> = {};
      const nextFilter: Record<string, string[]> = {};
      for (const [key, value] of Object.entries(next)) {
        if (highlightIds.has(key)) nextHighlight[key] = value;
        else nextFilter[key] = value;
      }
      setHighlightSelections(nextHighlight);
      setFilterSelections(nextFilter);
    },
    [highlightIds, setHighlightSelections, setFilterSelections]
  );

  /**
   * Combined watchlists that can filter alongside other lists — see
   * combinedFilters.ts. Until they load every combined watchlist counts as
   * relying on AND, which is the old one-at-a-time rule, never a looser one.
   */
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const orOnlyViewIds = useMemo(
    () =>
      new Set(
        (queriesQuery.data ?? [])
          .filter(isOrOnlyCombined)
          .map((query) => String(query.id))
      ),
    [queriesQuery.data]
  );

  const clearOwner = useCallback(
    (owner: FilterOwner) => {
      const cleared = Object.fromEntries(
        FILTER_OWNERS[owner].map((id) => [id, [] as string[]])
      );
      setFilterSelections({ ...filterSelections, ...cleared });
    },
    [filterSelections, setFilterSelections]
  );

  const { groups, passthrough } = useMemo(() => {
    const filterById = new Map(filterMenus.map((menu) => [menu.id, menu]));
    const highlightById = new Map(highlightMenus.map((menu) => [menu.id, menu]));
    const consumed = new Set<string>();

    // What can filter together. Lists, and combined watchlists that only use
    // OR, merge into one search (mergeCombinedFilters). A combined watchlist
    // relying on AND can't be merged without changing what it means, so it
    // filters on its own; the losing rows disable and say why rather than
    // leaving a caption for the user to police.
    const listFilterActive = FILTER_OWNERS.lists.some(
      (id) => (filterSelections[id]?.length ?? 0) > 0
    );
    const selectedViewIds = filterSelections["category-filter"] ?? [];
    const soloViewId = selectedViewIds.find((id) => !orOnlyViewIds.has(id));
    const viewFilterActive = soloViewId !== undefined;
    const activeViewId = soloViewId ?? selectedViewIds[0];
    const activeViewName = filterById
      .get("category-filter")
      ?.filterOptions.find((o) => String(o.id) === activeViewId)?.label;

    const note = (owner: FilterOwner) =>
      createElement(FilterConflictNote, {
        owner,
        activeName: owner === "views" ? activeViewName : undefined,
        activeId: owner === "views" ? activeViewId : undefined,
      });

    const countFor = (ids: string[]) =>
      ids.reduce((sum, id) => sum + (selections[id]?.length ?? 0), 0);

    // ── One table for every watchlist, whatever it holds ──────────────────
    const watchlistRows: MatrixRow[] = [];
    const watchlistKeys: string[] = [];
    for (const family of WATCHLIST_FAMILY) {
      const filterMenu = filterById.get(family.filterId);
      const highlightMenu = highlightById.get(family.highlightId);
      if (!filterMenu && !highlightMenu) continue;
      if (filterMenu) consumed.add(family.filterId);
      if (highlightMenu) consumed.add(family.highlightId);
      watchlistKeys.push(family.filterId, family.highlightId);

      // Union of both, so a list offered for only one verb still appears with
      // the other column disabled rather than vanishing.
      const seen = new Set<string>();
      for (const menu of [filterMenu, highlightMenu]) {
        for (const option of menu?.filterOptions ?? []) {
          const id = String(option.id);
          if (option.isHeader || seen.has(id)) continue;
          seen.add(id);
          watchlistRows.push({
            id,
            label: formatListName(option.label),
            group: ownerOf(family.filterId, id, menu),
            kind: family.kind,
            highlightSectionId: highlightMenu ? family.highlightId : undefined,
            filterSectionId: filterMenu ? family.filterId : undefined,
            filterDisabled: viewFilterActive || !!option.disabled,
            filterDisabledReason: viewFilterActive
              ? "A combined watchlist using AND is filtering — clear it to use individual lists"
              : undefined,
          });
        }
      }
    }

    // ── Combined watchlists (tags + keywords + lists with AND/OR) ─────────
    const combined = filterById.get("category-filter");
    const combinedRows: MatrixRow[] = [];
    if (combined) {
      consumed.add("category-filter");
      for (const option of combined.filterOptions) {
        if (option.isHeader) continue;
        const id = String(option.id);
        const orOnly = orOnlyViewIds.has(id);
        const othersFiltering =
          listFilterActive || selectedViewIds.some((viewId) => viewId !== id);
        combinedRows.push({
          id,
          label: formatListName(option.label),
          group: ownerOf("category-filter", id, combined),
          combined: true,
          highlightSectionId: "category-highlight",
          filterSectionId: "category-filter",
          /**
           * `transformSearchParams` takes a single `advancedFilter`, and a
           * second combined watchlist used to be silently ignored. Lists that
           * only use OR are now merged into that one filter, so they tick
           * freely; one relying on AND still can't share it, and says so
           * rather than looking applied while doing nothing.
           *
           * A selected row stays enabled so it can be unticked.
           */
          filterDisabled:
            !selectedViewIds.includes(id) &&
            (viewFilterActive || (!orOnly && othersFiltering)),
          filterDisabledReason: viewFilterActive
            ? "A combined watchlist using AND filters on its own — clear it to add more"
            : "This combined watchlist uses AND, so it filters on its own — clear the others to use it",
        });
      }
    }

    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
    const sortRows = (rows: MatrixRow[]) =>
      [...rows].sort(
        (a, b) =>
          // Your own lists first — they're the ones you reach for.
          (a.group === b.group ? 0 : a.group === "Yours" ? -1 : 1) ||
          collator.compare(a.label, b.label)
      );

    // One list of watchlists, however each one happens to be stored.
    //
    // "Combined watchlists" was a second group naming our own storage split:
    // whether a list is a flat set of terms or an AND/OR combination is our
    // problem, not something to make the reader classify before they can tick
    // a box. The rows are merged rather than dropped — each carries its own
    // filter/highlight section id, so routing is untouched and nothing the
    // desk already built becomes unreachable.
    const built: MatrixGroup[] = [];
    const listRows = [...watchlistRows, ...combinedRows];
    if (listRows.length > 0) {
      built.push({
        id: "watchlists-matrix",
        label: WATCHLIST_LABEL,
        rows: sortRows(listRows),
        // Only when something is actually held back.
        conflictNote: viewFilterActive
          ? note("views")
          : combinedRows.some((row) => row.filterDisabled)
            ? note("lists")
            : undefined,
        highlightCount: countFor([
          ...watchlistKeys.filter((k) => k.endsWith("-highlight")),
          "category-highlight",
        ]),
        filterCount: countFor([
          ...watchlistKeys.filter((k) => k.endsWith("-filter")),
          "category-filter",
        ]),
      });
    }

    // Sources, asset class — different axes, not lists. Left as they were.
    const rest = [...filterMenus, ...highlightMenus]
      .filter((menu) => !consumed.has(menu.id))
      .map((menu) => ({
        ...menu,
        filterOptions: viewFilterActive
          ? menu.filterOptions.map((o) => ({ ...o, disabled: true }))
          : menu.filterOptions,
      }));

    return { groups: built, passthrough: rest };
  }, [highlightMenus, filterMenus, filterSelections, selections, clearOwner, orOnlyViewIds]);

  const total = (state: Record<string, string[]>) =>
    Object.values(state).reduce((sum, ids) => sum + (ids?.length ?? 0), 0);

  return {
    groups,
    passthrough,
    selections,
    setSelections,
    highlightTotal: total(highlightSelections),
    filterTotal: total(filterSelections),
  };
}
